import { useMemo } from "react";
import { useDates } from "../../../dates/DatesContext";
import useSelectedMetrics from "../../../components/custom/merchandise/hooks/useSelectedMetrics";
import MultiMetricChart from "../../../components/custom/merchandise/MultiMetricChart";
import Loading from "../../../components/core/blocks/Loading";
import getMetricSpec from "../../merchandising/utils/metrics";
import DetailedMetric from "../../../components/core/blocks/DetailedMetric";
import { useAcosOrRoas } from "../../../utils/acosOrRoas";
import { useStandardEntityMetrics } from "../../merchandising/components/tables/metricUtils";
import type { SeriesLegendItemClickEventObject } from "highcharts/highcharts.src";
import moment from "moment";
import { useTranslation } from "react-i18next";
import ProductImage from "../../../components/custom/category/listings/ProductImage";
import { useQuery } from "@tanstack/react-query";
import { type ProductAdV4Row } from "../../perf_reports/api/product_ad/v4ProductAdReport";
import { useGetV4ProductAdReportByDate } from "../../perf_reports/api/product_ad/v4ProductAdReportByDate";
import { useGetV4ProductAdReportTotals } from "../../perf_reports/api/product_ad/v4ProductAdReportTotals";
import { VStack, HStack, Wrap } from "../../../../styled-system/jsx";
import { css } from "../../../../styled-system/css";
import { Tabs } from "../../../components/core/basic/Tabs";
import { Tab, TabList, TabPanel } from "react-aria-components";
import { SpotlightVideoSection } from "./spotlightVideos/SpotlightVideoSection";
import { AD_TYPES, SP_SPOTLIGHT_VIDEO_SUPPORTED_MARKETPLACES } from "../../../components/core/blocks/AdPlan/constants";
import { useAppSelector } from "../../../redux/hooks";

type Props = {
  row: ProductAdV4Row;
  productAdId: number;
};

const AdvertisementProductExpander = ({ row, productAdId }: Props) => {
  const { t } = useTranslation(["advertising"]);
  const acosOrRoas = useAcosOrRoas();
  const { marketPlace } = useAppSelector((state) => state.marketPlace);
  const isAmazonSponsoredProductAd = [AD_TYPES.SPONSORED_PRODUCT.value, AD_TYPES.SPONSORED_PRODUCT_AUTO.value].includes(
    row.ad_type,
  );
  const renderSpotlightVideoTab =
    isAmazonSponsoredProductAd && SP_SPOTLIGHT_VIDEO_SUPPORTED_MARKETPLACES.includes(marketPlace);

  const filters = [{ id: "product_advertisement_id", value: { op: "=", value: productAdId } }];

  const totalOptionsGetter = useGetV4ProductAdReportTotals();
  const productAdTotalsReportQuery = useQuery(
    totalOptionsGetter({
      filters: filters,
    }),
  );

  const { gvproduct_id: productId, gvadvertisement_id: adId, product_title: productTitle, asin, sku } = row;

  const { group, start, end } = useDates();

  const byDateOptionsGetter = useGetV4ProductAdReportByDate();
  const productAdByDateReportQuery = useQuery(
    byDateOptionsGetter({
      filters: filters,
    }),
  );

  const { selectedMetrics, toggleMetric } = useSelectedMetrics(["ad_sales", "ad_spend", "ctr"]);

  const standardMetricIds = useStandardEntityMetrics("productAd");

  const metrics = useMemo(() => {
    const preferredId = acosOrRoas("acos", "roas");
    return standardMetricIds
      .filter((id) => (["acos", "roas"].includes(id) ? id === preferredId : true))
      .slice(0, 6)
      .map(getMetricSpec);
  }, [acosOrRoas, standardMetricIds]);

  const handleLegendChange = (e: SeriesLegendItemClickEventObject) => {
    const id = e.target.options.custom?.id;
    if (id) toggleMetric(id);
    return false;
  };

  const timelineData = useMemo(() => {
    const data = productAdByDateReportQuery.data?.data;
    if (!data) return [];

    const dataByDate = new Map(data.map((row) => [row.start_date, row]));
    const unit =
      group === "month" ? "month"
      : group === "week" ? "week"
      : "day";
    const dates: { start_date: string; [key: string]: unknown }[] = [];
    const date = moment(start.format("YYYY-MM-DD"));
    if (unit === "week") {
      date.startOf("isoWeek");
    } else if (unit === "month") {
      date.startOf("month");
    }
    const endDate = moment(end.format("YYYY-MM-DD"));

    // This is a bit funky, but filling in missing dates because the query endpoint doesn't seem to for product ads,
    // whereas it does appear to for others
    while (date.isSameOrBefore(endDate, unit)) {
      const dateStr = date.format("YYYY-MM-DD");
      dates.push(dataByDate.get(dateStr) ?? { start_date: dateStr });
      date.add(1, unit);
    }

    return dates;
  }, [productAdByDateReportQuery.data?.data, group, start, end]);

  if (productAdByDateReportQuery.isLoading) {
    return <Loading height={"300px"} fullPage={false} />;
  }

  return (
    <VStack
      className={css({
        width: "100%",
        maxWidth: "100%",
        backgroundColor: "white",
        borderRadius: "lg",
        overflowX: "auto",
      })}
    >
      <HStack gap="large" paddingX="large" width="100%">
        <ProductImage height={100} width={100} id={String(productId)} />
        <VStack alignItems="flex-start" gap="xsmall">
          <h3
            className={css({
              marginBottom: "0",
              marginTop: "0",
              fontSize: "22px",
            })}
          >
            {productTitle}
          </h3>
          <span>
            {asin} ({sku})
          </span>
        </VStack>
      </HStack>

      <Tabs>
        <TabList>
          <Tab id="overview">{t(($) => $.advertising["Overview"])}</Tab>
          {renderSpotlightVideoTab && <Tab id="spotlight-videos">{t(($) => $.advertising["Spotlight Videos"])}</Tab>}
        </TabList>
        <TabPanel id="overview">
          <VStack width="100%" alignItems="stretch" paddingX="xlarge" paddingBottom="large">
            <MultiMetricChart
              metrics={metrics}
              selectedMetrics={selectedMetrics}
              data={timelineData}
              group={group}
              handleLegendChange={handleLegendChange}
              showAnnotations={false}
            />
            <Wrap gap="medium" width="100%" paddingTop="large">
              {metrics.slice(0, 6).map((m) => (
                <VStack key={m.id} className={css({ flex: "1", minWidth: "150px" })}>
                  <DetailedMetric center metric={m} data={productAdTotalsReportQuery.data?.data} showTarget={false} />
                </VStack>
              ))}
            </Wrap>
          </VStack>
        </TabPanel>
        {renderSpotlightVideoTab && (
          <TabPanel id="spotlight-videos">
            <VStack
              alignItems="center"
              justifyContent="center"
              width="100%"
              minHeight="200px"
              paddingX="xlarge"
              paddingBottom="large"
            >
              <SpotlightVideoSection adId={adId} asin={asin} />
            </VStack>
          </TabPanel>
        )}
      </Tabs>
    </VStack>
  );
};

export default AdvertisementProductExpander;
